import React from 'react';
import { Bot, Zap, Search, Shuffle, Rocket, GitBranch, Mic, Calendar, Target, Globe, BarChart3, CalendarDays, Image as ImageIcon, ShieldCheck, Beaker, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DifferentiatorsSection = () => {
  const { t } = useTranslation();
  const itemDefs = [
    { id: 'verifyLive',      icon: Beaker,      iconBg: 'from-[#F59E0B] to-[#EC4899]', featured: true, isNew: true },
    { id: 'businessProfile', icon: Building2,   iconBg: 'from-[#0055FF] to-[#10B981]', featured: true, isFoundation: true },
    { id: 'llmsTxt',         icon: Bot,         iconBg: 'from-[#10B981] to-[#00E5FF]', featured: true },
    { id: 'brandVoice',      icon: Mic,         iconBg: 'from-[#8B5CF6] to-[#EC4899]', featured: true },
    { id: 'autoPilot',       icon: Calendar,    iconBg: 'from-[#0055FF] to-[#06B6D4]', featured: true },
    { id: 'analytics',       icon: BarChart3,   iconBg: 'from-[#06B6D4] to-[#0055FF]', featured: true },
    { id: 'contentCalendar', icon: CalendarDays,iconBg: 'from-[#8B5CF6] to-[#0055FF]' },
    { id: 'imageSeo',        icon: ImageIcon,   iconBg: 'from-[#F59E0B] to-[#EC4899]' },
    { id: 'refund',          icon: ShieldCheck, iconBg: 'from-[#10B981] to-[#047857]' },
    { id: 'keywordResearch', icon: Target,      iconBg: 'from-[#F59E0B] to-[#EC4899]' },
    { id: 'cannibalization', icon: GitBranch,   iconBg: 'from-[#F59E0B] to-[#EF4444]' },
    { id: 'ai404',           icon: Shuffle,     iconBg: 'from-[#0055FF] to-[#8B5CF6]' },
    { id: 'multilang',       icon: Globe,       iconBg: 'from-[#10B981] to-[#0055FF]' },
    { id: 'migration',       icon: Rocket,      iconBg: 'from-[#047857] to-[#10B981]' },
    { id: 'nativeStack',     icon: Zap,         iconBg: 'from-[#00E5FF] to-[#0055FF]' },
  ];

  const items = itemDefs.map(d => {
    const bullets = t(`differentiators.cards.${d.id}.bullets`, { returnObjects: true });
    return {
      ...d,
      title: t(`differentiators.cards.${d.id}.title`),
      tagline: t(`differentiators.cards.${d.id}.tagline`),
      desc: t(`differentiators.cards.${d.id}.desc`),
      bullets: Array.isArray(bullets) ? bullets : [],
    };
  });

  return (
    <section className="py-24 px-6 bg-[#0a0a0a] border-t border-white/5" data-testid="differentiators-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-fade-up">
          <span className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-semibold">{t('differentiators.eyebrow')}</span>
          <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-medium tracking-tight mt-4 mb-6">
            {t('differentiators.title1')}<span className="text-gradient">{t('differentiators.title2')}</span>{t('differentiators.title3')}
          </h2>
          <p className="text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            {t('differentiators.subtitle')} <strong className="text-white">{t('differentiators.subtitleHighlight')}</strong>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it, idx) => {
            const Icon = it.icon;
            const badgeBg = it.isNew
              ? 'bg-gradient-to-r from-[#F59E0B] to-[#EC4899] text-white'
              : it.isFoundation
                ? 'bg-gradient-to-r from-[#10B981] to-[#0055FF] text-white'
                : it.featured
                  ? 'bg-gradient-to-r from-[#10B981] to-[#00E5FF] text-black'
                  : 'bg-white/10 text-white/70';
            return (
              <div
                key={it.id}
                className={`relative rounded-2xl p-6 border transition-all hover:-translate-y-1 ${
                  it.isNew
                    ? 'border-[#F59E0B]/50 bg-gradient-to-br from-[#F59E0B]/[0.10] to-[#EC4899]/[0.05]'
                    : it.isFoundation
                      ? 'border-[#10B981]/50 bg-gradient-to-br from-[#10B981]/[0.10] to-[#0055FF]/[0.05]'
                      : it.featured
                        ? 'border-[#10B981]/40 bg-gradient-to-br from-[#10B981]/[0.08] to-[#00E5FF]/[0.04]'
                        : 'border-white/10 bg-white/[0.02] hover:border-white/20'
                } animate-fade-up`}
                style={{ animationDelay: `${idx * 0.06}s` }}
                data-testid={`diff-card-${idx}`}
              >
                {it.tagline && (
                  <div className="absolute -top-2 right-4">
                    <span className={`px-2 py-1 rounded-full text-[9px] uppercase tracking-wider font-bold ${badgeBg}`}>
                      {it.tagline}
                    </span>
                  </div>
                )}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${it.iconBg} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-['Outfit'] font-medium mb-3">{it.title}</h3>
                <p className="text-sm text-[#A1A1AA] mb-4 leading-relaxed">{it.desc}</p>
                <ul className="space-y-1.5">
                  {it.bullets.map((b, i) => (
                    <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                      <span className="text-[#0055FF] mt-0.5">→</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center animate-fade-up">
          <p className="text-base text-[#A1A1AA] mb-6">{t('differentiators.ctaText')}</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register">
              <button className="px-8 py-3 rounded-full bg-[#0055FF] hover:bg-[#3377FF] text-white font-medium transition-colors">
                {t('differentiators.ctaPrimary')}
              </button>
            </Link>
            <a href="#features">
              <button className="px-8 py-3 rounded-full border border-white/20 text-white hover:bg-white/5 font-medium transition-colors">
                {t('differentiators.ctaSecondary')}
              </button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DifferentiatorsSection;
